import { Col, Row, Table } from 'react-bootstrap';
import { HIGH_RISK, LOW_RISK, MEDIUM_RISK } from '../../utils';

const getActiveShootColour = (value) => {
  if (value == null || isNaN(value)) return undefined;
  if (value <= 20) return LOW_RISK;
  if (value <= 40) return MEDIUM_RISK;
  return HIGH_RISK;
};

const getInfestedColour = (value) => {
  if (value == null || isNaN(value)) return undefined;
  if (value <= 2) return LOW_RISK;
  if (value <= 4) return MEDIUM_RISK;
  return HIGH_RISK;
};

const getOverallRiskFactorColour = (riskFactor) => {
  switch (riskFactor) {
    case 'low':
      return LOW_RISK;
    case 'medium':
      return MEDIUM_RISK;
    case 'high':
      return HIGH_RISK;
    default:
      return undefined;
  }
};

const RiskAssessment = ({ monitoringBlock, riskAssessmentData }) => {
  const activeLabels = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const infestedLabels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const riskAssessment = riskAssessmentData[0];

  if (!riskAssessment) {
    return (
      <div className='mb-4 mb-md-0'>
        <div className='mb-1'>
          <h5>Risk Assessment</h5>
        </div>
        <p className='text-muted mb-0'>No risk assessment data available.</p>
      </div>
    );
  }

  const activePercent = riskAssessment.active_percent;
  const percentNewLeafDamage = riskAssessment.percent_new_leaf_damage;
  const overallRiskFactorColour = getOverallRiskFactorColour(
    riskAssessment.risk_factor
  );

  const activeSteps = Array.from({ length: 100 }, (_, i) => i + 1);
  const infestedSteps = Array.from({ length: 100 }, (_, i) => (i + 1) / 10);

  // Risk level boundaries for active shoot - low 20, medium 40, high 50+
  const activeRiskBorders = [20, 40];
  // Risk level boundaries for infested - low 2, medium 4, high 5+
  const infestedRiskBorders = [20, 40]; // mapped to 100-column scale (2/10*100=20, 4/10*100=40)

  const activeColour = getActiveShootColour(activePercent);
  const infestedColour = getInfestedColour(percentNewLeafDamage);

  const getActiveCellBorder = (step) => {
    if (activeRiskBorders.includes(step)) return '2px solid rgba(0, 0, 0, 0.5)';
    if (step % 10 === 0) return '1px solid rgba(0, 0, 0, 0.15)';
    return 'none';
  };

  const getInfestedCellBorder = (step) => {
    if (infestedRiskBorders.includes(step))
      return '2px solid rgba(0, 0, 0, 0.5)';
    if (step % 10 === 0) return '1px solid rgba(0, 0, 0, 0.15)';
    return 'none';
  };

  const getActiveLabelBorder = (al) => {
    if (activeRiskBorders.includes(al)) return '2px solid rgba(0, 0, 0, 0.5)';
    return '1px solid rgba(0, 0, 0, 0.15)';
  };

  const getInfestedLabelBorder = (index) => {
    if (infestedRiskBorders.includes((index + 1) * 10))
      return '2px solid rgba(0, 0, 0, 0.5)';
    return '1px solid rgba(0, 0, 0, 0.15)';
  };

  return (
    <div className='mb-4 mb-md-0'>
      <div className='mb-1'>
        <h5>Risk Assessment</h5>
      </div>
      <Row>
        <Col xs={10}>
          <div
            style={{
              height: '100%'
            }}
          >
            <Table
              className='mb-0'
              borderless
              size='sm'
              style={{ tableLayout: 'fixed', height: '100%' }}
            >
              <tbody>
                <tr>
                  <td
                    rowSpan={2}
                    className='align-top font-weight-bold'
                    style={{
                      fontSize: '14px',
                      width: '85px',
                      boxShadow: '1px 0 0 0 lightgray'
                    }}
                  >
                    % Active Shoot
                  </td>
                  {activeSteps.map((step) => (
                    <td
                      key={step}
                      style={{
                        backgroundColor:
                          step <= activePercent ? activeColour : 'transparent',
                        padding: 0,
                        height: '24px',
                        borderRight: getActiveCellBorder(step)
                      }}
                    />
                  ))}
                </tr>
                <tr>
                  {activeLabels.map((al) => (
                    <td
                      key={al}
                      colSpan={10}
                      style={{
                        textAlign: 'right',
                        fontSize: '11px',
                        borderRight: getActiveLabelBorder(al)
                      }}
                    >
                      {al}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td colSpan={101} style={{ padding: 0 }}>
                    <hr style={{ margin: 0, borderColor: 'lightgray' }} />
                  </td>
                </tr>
                <tr>
                  <td
                    rowSpan={2}
                    className='align-top font-weight-bold'
                    style={{
                      fontSize: '14px',
                      width: '85px',
                      boxShadow: '1px 0 0 0 lightgray'
                    }}
                  >
                    % ALCM Infested
                  </td>
                  {infestedSteps.map((step, i) => (
                    <td
                      key={step}
                      style={{
                        backgroundColor:
                          step <= percentNewLeafDamage
                            ? infestedColour
                            : 'transparent',
                        padding: 0,
                        height: '24px',
                        borderRight: getInfestedCellBorder(i + 1)
                      }}
                    />
                  ))}
                </tr>
                <tr>
                  {infestedLabels.map((il, index) => (
                    <td
                      key={il}
                      colSpan={10}
                      style={{
                        textAlign: 'right',
                        fontSize: '11px',
                        borderRight: getInfestedLabelBorder(index)
                      }}
                    >
                      {il}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          </div>
        </Col>
        <Col xs={2} className='pl-0'>
          <div className='d-flex flex-column' style={{ height: '100%' }}>
            <h6>Overall Risk</h6>
            <div
              style={{
                backgroundColor: overallRiskFactorColour,
                width: '65%',
                aspectRatio: '1',
                borderRadius: '4px'
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default RiskAssessment;

